"use client"
import { Text } from "@/elements"
import clsx from "clsx"
import {
  CountryCode as LibphonenumberCountryCode,
  formatIncompletePhoneNumber,
  getExampleNumber,
  PhoneNumber,
} from "libphonenumber-js/mobile"
import examples from "libphonenumber-js/mobile/examples"
import { useEffect, useMemo, useState } from "react"

import { RoundedInput } from "../RoundedInput"
import CountryCodeSelector from "./CountryCodeSelector"
import { MOBILE_CALLING_CODES, CountryCode } from "./mobileCallingCodes"

type PhoneNumberValues = {
  value: string
  display: string
}

export const PhoneNumberInput = ({
  setPhoneNumberWithCode,
}: {
  setPhoneNumberWithCode: (phoneNumber: string) => void
}) => {
  const [countryCode, setCountryCode] = useState<CountryCode>("US")
  const [examplePhoneNumber, setExamplePhoneNumber] = useState<PhoneNumber | undefined>(undefined)
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumberValues>({
    value: "",
    display: "",
  })

  const formatNumber = (phoneNumber: string, countryCode: CountryCode, cc: string) => {
    const formattedPhoneNumber = formatIncompletePhoneNumber(
      `+${MOBILE_CALLING_CODES[countryCode].callingCode}${phoneNumber}`,
      {
        defaultCountry: countryCode,
        defaultCallingCode: cc,
      }
    )
    const countryCallingCodePattern = new RegExp(`^\\+${MOBILE_CALLING_CODES[countryCode].callingCode}\\s*`) // Match the calling code at the beginning followed by any number of spaces
    const strippedNumber = formattedPhoneNumber.replace(countryCallingCodePattern, "").trim()

    return {
      formattedNumber: strippedNumber,
      internationalNumber: formattedPhoneNumber,
    }
  }

  const { exampleInternationalNumber, exampleNationalNumber } = useMemo(() => {
    if (examplePhoneNumber) {
      const exampleInternationalNumber = examplePhoneNumber?.formatInternational()

      const countryCallingCodePattern = new RegExp(`^\\+${MOBILE_CALLING_CODES[countryCode].callingCode}\\s*`) // Match the calling code at the beginning followed by any number of spaces
      const strippedNumber = exampleInternationalNumber.replace(countryCallingCodePattern, "").trim()

      return {
        exampleInternationalNumber,
        exampleNationalNumber: strippedNumber,
      }
    }
    return {
      exampleInternationalNumber: "",
      exampleNationalNumber: "",
    }
  }, [examplePhoneNumber, countryCode])

  const onPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value
    const data = MOBILE_CALLING_CODES[countryCode]

    const { formattedNumber, internationalNumber } = formatNumber(text, data.countryCode, data.callingCode)

    // Prevent entering in more digits than the number allows
    const internationalNumberWithoutWhiteSpace = internationalNumber.replaceAll(" ", "")
    const exampleNumberWithoutWhiteSpace = exampleInternationalNumber?.replaceAll(" ", "")
    if (
      exampleNumberWithoutWhiteSpace &&
      internationalNumberWithoutWhiteSpace.length > exampleNumberWithoutWhiteSpace.length
    ) {
      return
    }

    // We need to only allow formatting if the last character is a number otherwise you can't delete special chars
    if (/^[0-9]+$/.test(formattedNumber[formattedNumber.length - 1]) && text.length > phoneNumber.value.length) {
      setPhoneNumber({
        value: text.replace(/[^0-9]/g, ""),
        display: formattedNumber,
      })
    } else {
      setPhoneNumber({
        value: text.replace(/[^0-9]/g, ""),
        display: text,
      })
    }
  }

  const onSelectCountryCode = (code: CountryCode) => {
    setCountryCode(code)
    const data = MOBILE_CALLING_CODES[code]
    const { formattedNumber } = formatNumber(phoneNumber.value, data.countryCode, data.callingCode)

    setPhoneNumber({
      value: phoneNumber.value,
      display: formattedNumber,
    })
  }

  useEffect(() => {
    if (examples) {
      const exampleNumber = getExampleNumber(countryCode, examples)
      if (exampleNumber) {
        setExamplePhoneNumber(exampleNumber)
      }
    }
  }, [countryCode, examples])

  useEffect(() => {
    setPhoneNumberWithCode(`+${MOBILE_CALLING_CODES[countryCode].callingCode}${phoneNumber.value}`)
  }, [phoneNumber.value, countryCode])

  return (
    <div className="relative flex w-full flex-row items-center gap-x-3">
      <CountryCodeSelector setValue={onSelectCountryCode} value={countryCode} />
      <div className="relative w-full">
        <div className="absolute top-0 rounded-full border border-transparent bg-transparent px-9 py-4 placeholder-fg-3 outline-none transition-all focus:outline-none focus:ring-0 md:text-body1-md lg:text-body1-lg">
          +{MOBILE_CALLING_CODES[countryCode].callingCode}
        </div>
        <RoundedInput
          value={phoneNumber.display}
          className={clsx("ml-auto w-full", ["US", "CA"].includes(countryCode) ? "pl-14" : "pl-[67px]")}
          onChange={onPhoneNumberChange}
          placeholder={exampleNationalNumber}
          type="tel"
        />
      </div>
    </div>
  )
}
